// Tiny fetch wrapper. Web build hits the proxy at the same origin (relative
// /api). Native (Capacitor) build sets VITE_API_BASE at build time so the
// WebView (origin capacitor://localhost) can reach the LAN backend.
//
// Auth: Keycloak access tokens (RS256). The SPA stores `access_token` +
// `refresh_token` + `expires_at`, sends the access_token as
// `Authorization: Bearer`, and proactively refreshes via /api/auth/refresh
// before expiry. A 401 from any other endpoint also triggers one refresh
// attempt before bubbling up to the user-facing logout.

const API_BASE = (import.meta.env.VITE_API_BASE ?? "").replace(/\/$/, "");
const TOKEN_KEY = "finance_jwt";
const REFRESH_KEY = "finance_refresh";
const EXPIRES_KEY = "finance_expires";
// Refresh proactively when the token has less than this many seconds left.
const REFRESH_LEEWAY_SEC = 30;

interface StoredTokens {
  access: string;
  refresh: string | null;
  expiresAt: number; // epoch seconds; 0 if unknown
}

function readStorage(key: string): string | null {
  try { return localStorage.getItem(key); } catch { return null; }
}
function writeStorage(key: string, val: string | null) {
  try {
    if (val === null) localStorage.removeItem(key);
    else localStorage.setItem(key, val);
  } catch {}
}

export const tokenStore = {
  get: (): StoredTokens | null => {
    const access = readStorage(TOKEN_KEY);
    if (!access) return null;
    const refresh = readStorage(REFRESH_KEY);
    const expiresAt = Number(readStorage(EXPIRES_KEY) || "0");
    return { access, refresh, expiresAt };
  },
  set: (tokens: { access_token: string; refresh_token?: string | null; expires_in?: number }) => {
    writeStorage(TOKEN_KEY, tokens.access_token);
    if (tokens.refresh_token !== undefined) {
      writeStorage(REFRESH_KEY, tokens.refresh_token || null);
    }
    const exp = tokens.expires_in ? Math.floor(Date.now() / 1000) + tokens.expires_in : 0;
    writeStorage(EXPIRES_KEY, String(exp));
  },
  clear: () => {
    writeStorage(TOKEN_KEY, null);
    writeStorage(REFRESH_KEY, null);
    writeStorage(EXPIRES_KEY, null);
    // Session ends → the persisted identity + data caches (lib/auth.tsx,
    // lib/swr.ts) must not survive into the next session on this device.
    writeStorage("logbuch:me:v1", null);
    writeStorage("logbuch:swr:v1", null);
  },
};

export function apiUrl(path: string): string {
  const p = path.startsWith("/") ? path : `/${path}`;
  return `${API_BASE}${p}`;
}

/**
 * Absolute base for links that leave the app (Einladungslinks, geteilte URLs).
 * Im nativen Build ist window.location.origin das Capacitor-WebView
 * (https://localhost) — dort zeigt API_BASE auf die öffentliche Domain, die
 * App und API teilen. Im Web-Build (API_BASE leer) stimmt origin.
 */
export function publicBaseUrl(): string {
  return API_BASE || window.location.origin;
}

/**
 * Structured backend-error shape. The backend wraps user-facing errors as
 *   { detail: { error: "stable_code", message: "Deutscher Satz", hint?: "…" } }
 * but legacy endpoints + some FastAPI internals still emit a plain string detail.
 * `ApiError` normalises both into the same fields so callers don't have to.
 */
export class ApiError extends Error {
  status: number;
  body: unknown;
  /** Stable machine-readable error code (snake_case). `unknown` for unstructured. */
  code: string;
  /** German user-facing message. Falls back to a generic line. */
  userMessage: string;
  /** Optional supporting line for the UI; null when the backend didn't send one. */
  hint: string | null;

  constructor(status: number, body: unknown) {
    const { code, message, hint } = ApiError._extract(status, body);
    super(message);
    this.status = status;
    this.body = body;
    this.code = code;
    this.userMessage = message;
    this.hint = hint;
  }

  private static _extract(
    status: number,
    body: unknown,
  ): { code: string; message: string; hint: string | null } {
    // Structured: { detail: { error, message, hint } }
    if (body && typeof body === "object" && "detail" in body) {
      const d = (body as { detail: unknown }).detail;
      if (d && typeof d === "object") {
        const obj = d as { error?: unknown; message?: unknown; hint?: unknown };
        const code = typeof obj.error === "string" ? obj.error : "unknown";
        const message =
          typeof obj.message === "string" && obj.message.trim().length > 0
            ? obj.message
            : ApiError._defaultMessage(status);
        const hint = typeof obj.hint === "string" ? obj.hint : null;
        return { code, message, hint };
      }
      // Legacy: detail is a plain string ("user_already_in_household")
      if (typeof d === "string") {
        return {
          code: d,
          message: ApiError._defaultMessage(status, d),
          hint: null,
        };
      }
    }
    return { code: "unknown", message: ApiError._defaultMessage(status), hint: null };
  }

  private static _defaultMessage(status: number, code?: string): string {
    // Fallback copy when the backend hasn't given us a German line yet.
    if (status === 401) return "Du bist nicht (mehr) angemeldet.";
    if (status === 403) return "Dazu hast du keine Berechtigung.";
    if (status === 404) return "Nicht gefunden.";
    if (status === 409) return code ? `Konflikt: ${code}` : "Das geht so nicht — Konflikt mit dem aktuellen Stand.";
    if (status === 410) return "Nicht mehr gültig.";
    if (status === 429) return "Zu viele Versuche — bitte kurz warten.";
    if (status >= 500) return "Der Server hat gerade ein Problem.";
    if (status === 0) return "Keine Verbindung — bist du offline?";
    return code ? `Fehler ${status} (${code})` : `Fehler ${status}`;
  }
}

/**
 * Render any thrown value into a German one-liner suitable for a toast.
 * Use this in `.catch(e => setError(errMessage(e)))`.
 */
export function errMessage(err: unknown): string {
  if (err instanceof ApiError) {
    return err.hint ? `${err.userMessage} — ${err.hint}` : err.userMessage;
  }
  if (err instanceof Error) return err.message || "Unbekannter Fehler.";
  return String(err);
}

// Single in-flight refresh promise — coalesces parallel API calls so we don't
// hit Keycloak with concurrent refresh_token uses (which it rejects after the
// first one because each refresh rotates the token).
let _refreshing: Promise<StoredTokens | null> | null = null;

async function refreshIfStale(): Promise<StoredTokens | null> {
  const cur = tokenStore.get();
  if (!cur || !cur.refresh) return cur;
  const now = Math.floor(Date.now() / 1000);
  if (cur.expiresAt > now + REFRESH_LEEWAY_SEC) return cur;
  return doRefresh(cur.refresh);
}

async function doRefresh(refreshToken: string): Promise<StoredTokens | null> {
  if (_refreshing) return _refreshing;
  _refreshing = (async () => {
    try {
      const res = await fetch(apiUrl("/api/auth/refresh"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh_token: refreshToken }),
        credentials: "include",
      });
      if (!res.ok) {
        // Refresh failed (invalid_grant, expired): tokens are useless.
        tokenStore.clear();
        return null;
      }
      const body = await res.json() as { access_token: string; refresh_token: string; expires_in: number };
      tokenStore.set(body);
      return tokenStore.get();
    } finally {
      _refreshing = null;
    }
  })();
  return _refreshing;
}

export async function api<T = unknown>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const tok = await refreshIfStale();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...((init.headers as Record<string, string>) ?? {}),
  };
  if (tok && !headers.Authorization) headers.Authorization = `Bearer ${tok.access}`;
  let res = await fetch(apiUrl(path), {
    credentials: "include",
    ...init,
    headers,
  });
  // 401 + we have a refresh token + we haven't retried yet → one more attempt
  // after a forced refresh. Catches the "expires_at was wrong / token revoked"
  // edge cases without bouncing the user out.
  if (res.status === 401 && tok?.refresh && !init.headers) {
    const fresh = await doRefresh(tok.refresh);
    if (fresh) {
      const retryHeaders = { ...headers, Authorization: `Bearer ${fresh.access}` };
      res = await fetch(apiUrl(path), {
        credentials: "include",
        ...init,
        headers: retryHeaders,
      });
    }
  }
  const text = await res.text();
  // Defensive parse — a TLS-terminator or gateway can serve a plain-HTML
  // error page on transient failures. We don't want a JSON.parse SyntaxError
  // bubbling up as a crash; wrap it in a sensible ApiError instead.
  let body: unknown = null;
  if (text) {
    try {
      body = JSON.parse(text);
    } catch {
      body = { detail: text.slice(0, 200) };
    }
  }
  if (!res.ok) throw new ApiError(res.status, body);
  return body as T;
}

export interface SubscriptionInfo {
  status: string;
  plan: string | null;
  has_access: boolean;
  current_period_end: string | null;
  trial_end: string | null;
  provider: string | null;
  cancel_at_period_end: boolean;
  subscription_id: string | null;
  billing_disabled: boolean;
}

export interface HouseholdSummary {
  id: number;
  name: string;
  role: string;
  is_active: boolean;
  setup_complete: boolean;
  goal_theme: string;
  long_term_goal_name: string;
}

export interface User {
  id: number;
  email: string;
  created_at: string;
  household_id: number | null;
  needs_setup: boolean;
  households: HouseholdSummary[];
  subscription: SubscriptionInfo | null;
  is_admin: boolean;
  /** Wann der Datenschutzerklärung zugestimmt wurde — null zeigt das PrivacyGate. */
  privacy_accepted_at: string | null;
}

export interface OAuthInfo {
  enabled: boolean;
  issuer: string | null;
  client_id: string | null;
}

export const auth = {
  me: () => api<User>("/api/auth/me"),
  acceptPrivacy: () => api<User>("/api/auth/accept-privacy", { method: "POST" }),
  logout: async () => {
    // Hand the refresh_token to the backend so it can run a backchannel
    // logout against Keycloak — otherwise the SSO session lives on and the
    // next /oauth/login silently re-auths the user.
    const rt = tokenStore.get()?.refresh ?? null;
    try {
      return await api<{ ok: true }>("/api/auth/logout", {
        method: "POST",
        body: JSON.stringify({ refresh_token: rt }),
      });
    } finally {
      tokenStore.clear();
    }
  },
  oauthInfo: () => api<OAuthInfo>("/api/auth/oauth/info"),
  oauthLoginUrl: (returnTo?: string) => {
    const u = new URL(apiUrl("/api/auth/oauth/login"), window.location.href);
    if (returnTo) u.searchParams.set("return_to", returnTo);
    return u.toString();
  },
  oauthRegisterUrl: (returnTo?: string) => {
    const u = new URL(apiUrl("/api/auth/oauth/register"), window.location.href);
    if (returnTo) u.searchParams.set("return_to", returnTo);
    return u.toString();
  },
  /** Parse the URL hash from the OIDC callback redirect and stash the tokens.
   *  The callback hands us back `#access_token=…&refresh_token=…&expires_in=…`.
   *  Returns true if tokens were found (caller should then strip the hash). */
  consumeCallbackHash: (): boolean => {
    if (typeof window === "undefined" || !window.location.hash) return false;
    return auth.consumeOidcFragment(window.location.hash);
  },
  /** Same as `consumeCallbackHash` but takes an arbitrary fragment (or full
   *  URL containing one). Used by the Capacitor `appUrlOpen` deep-link handler
   *  which receives `de.overfit.logbuch://oauth-callback#access_token=…`. */
  consumeOidcFragment: (urlOrFragment: string): boolean => {
    const hashIdx = urlOrFragment.indexOf("#");
    const raw = hashIdx >= 0 ? urlOrFragment.slice(hashIdx + 1) : urlOrFragment;
    if (!raw) return false;
    const params = new URLSearchParams(raw);
    const access = params.get("access_token");
    if (!access) return false;
    tokenStore.set({
      access_token: access,
      refresh_token: params.get("refresh_token"),
      expires_in: Number(params.get("expires_in") || "0"),
    });
    return true;
  },
};

// ----- Household / members -----

export interface HouseholdMember {
  id: number;
  household_id: number;
  user_id: number | null;
  display_name: string;
  color: string;
  sort_order: number;
  archived: boolean;
  role: string;
}

export interface Household {
  id: number;
  name: string;
  long_term_goal_name: string;
  goal_theme: string;
  setup_complete: boolean;
  ai_features_enabled: boolean;
  budget_start_date: string | null;
  members: HouseholdMember[];
}

export interface Invite {
  id: number;
  household_id: number;
  token: string;
  uses_remaining: number;
  revoked: boolean;
  created_at: string;
}

export const household = {
  get: () => api<Household>("/api/household"),
  create: (body: { name: string; long_term_goal_name: string; goal_theme: string }) =>
    api<Household>("/api/household", { method: "POST", body: JSON.stringify(body) }),
  update: (
    body: Partial<{
      name: string;
      long_term_goal_name: string;
      goal_theme: string;
      setup_complete: boolean;
      ai_features_enabled: boolean;
      budget_start_date: string | null;
    }>,
  ) => api<Household>("/api/household", { method: "PATCH", body: JSON.stringify(body) }),
  members: () => api<HouseholdMember[]>("/api/household/members"),
  addMember: (body: { display_name: string; color: string; sort_order?: number }) =>
    api<HouseholdMember>("/api/household/members", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  updateMember: (
    id: number,
    body: Partial<{ display_name: string; color: string; sort_order: number; archived: boolean; role: string }>,
  ) =>
    api<HouseholdMember>(`/api/household/members/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  archiveMember: (id: number) =>
    api(`/api/household/members/${id}`, { method: "DELETE" }),
  invites: () => api<Invite[]>("/api/household/invites"),
  createInvite: (uses_remaining: number = 5) =>
    api<Invite>(`/api/household/invites?uses_remaining=${uses_remaining}`, {
      method: "POST",
    }),
  revokeInvite: (id: number) =>
    api(`/api/household/invites/${id}`, { method: "DELETE" }),
  join: (token: string) =>
    api<Household>("/api/household/join", {
      method: "POST",
      body: JSON.stringify({ token }),
    }),
  /** Retroactive budget-start cutoff: preview + owner-only purge. */
  purgeBeforeStartPreview: () =>
    api<{ budget_start_date: string | null; count: number; earliest: string | null }>(
      "/api/household/purge-before-start/preview",
    ),
  purgeBeforeStart: () =>
    api<{ deleted: number }>("/api/household/purge-before-start", { method: "POST" }),
  /** All households the current user is a member of — drives the topbar switcher. */
  list: () => api<Household[]>("/api/household/list"),
  /** Switch the active household. Returns the freshly-active household. */
  switch: (household_id: number) =>
    api<Household>("/api/household/switch", {
      method: "POST",
      body: JSON.stringify({ household_id }),
    }),
};

// ----- Depot (Wertpapierdepot) -----

export interface DepotBroker {
  id: string;
  label: string;
  description: string;
}

export interface DepotAccount {
  id: number;
  name: string;
  bank_name: string | null;
  currency: string;
  balance_cached: string;
  total_value: string;
  holdings_count: number;
  last_sync_at: string | null;
  is_manual: boolean;
}

export interface DepotHolding {
  id: number;
  account_id: number;
  isin: string | null;
  wkn: string | null;
  name: string;
  quantity: string;
  price: string;
  price_currency: string;
  market_value: string;
  /** Total amount paid for the position. null when unknown. */
  cost_basis: string | null;
  as_of: string;
}

export interface DepotSnapshot {
  snapshot_date: string;
  total_value: string;
}

export const depot = {
  brokers: () => api<DepotBroker[]>("/api/depot/brokers"),
  listAccounts: () => api<DepotAccount[]>("/api/depot/accounts"),
  createManualAccount: (body: { name: string; broker_id: string; currency?: string }) =>
    api<DepotAccount>("/api/depot/accounts", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  deleteAccount: (id: number) =>
    api(`/api/depot/accounts/${id}`, { method: "DELETE" }),
  holdings: (accountId: number) =>
    api<DepotHolding[]>(`/api/depot/${accountId}/holdings`),
  addHolding: (
    accountId: number,
    body: {
      isin?: string | null;
      wkn?: string | null;
      name: string;
      quantity: string;
      price: string;
      price_currency?: string;
      market_value?: string | null;
      /** Total Einstandswert. */
      cost_basis?: string | null;
      /** Convenience: per-share buy price; backend multiplies by quantity. */
      buy_price?: string | null;
    },
  ) =>
    api<DepotHolding>(`/api/depot/${accountId}/holdings`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  updateHolding: (
    id: number,
    body: Partial<{
      isin: string | null;
      wkn: string | null;
      name: string;
      quantity: string;
      price: string;
      price_currency: string;
      market_value: string;
      cost_basis: string | null;
      buy_price: string | null;
    }>,
  ) =>
    api<DepotHolding>(`/api/depot/holdings/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  deleteHolding: (id: number) =>
    api(`/api/depot/holdings/${id}`, { method: "DELETE" }),
  history: (accountId: number, range: "3m" | "6m" | "12m" | "3y" | "all" = "12m") =>
    api<DepotSnapshot[]>(`/api/depot/${accountId}/history?range=${range}`),
  forceSnapshot: (accountId: number) =>
    api<DepotSnapshot>(`/api/depot/${accountId}/snapshot`, { method: "POST" }),
};

// ----- Feedback / problem reports -----

export type FeedbackType = "problem" | "feature" | "other";
export type FeedbackCategory =
  | "konto"
  | "depot"
  | "allgemein"
  | "import"
  | "sparziel"
  | "anderes";
export type FeedbackStatus = "open" | "ack" | "closed";

export interface FeedbackOut {
  id: number;
  household_id: number | null;
  user_id: number | null;
  type: FeedbackType;
  category: FeedbackCategory;
  subject: string;
  body: string;
  context: Record<string, unknown> | null;
  status: FeedbackStatus;
  admin_note: string | null;
  reporter_email: string | null;
  created_at: string;
  updated_at: string;
}

export interface AnalyticsDayPoint {
  date: string;
  views: number;
  uniques: number;
}
export interface AnalyticsPathPoint {
  path: string;
  views: number;
}
export interface AnalyticsReferrerPoint {
  domain: string;
  views: number;
}
export interface AnalyticsSource {
  source: "logbuch-landing" | "logbuch-app";
  views_30d: number;
  uniques_30d: number;
  bots_30d: number;
  daily: AnalyticsDayPoint[];
  top_paths: AnalyticsPathPoint[];
  top_referrers: AnalyticsReferrerPoint[];
}
export interface AnalyticsSummary {
  enabled: boolean;
  sources: AnalyticsSource[];
}

export const analytics = {
  /** Admin-only — non-admins get 403. */
  summary: () => api<AnalyticsSummary>("/api/analytics/summary"),
};

export const feedback = {
  create: (body: {
    type: FeedbackType;
    category: FeedbackCategory;
    subject: string;
    body: string;
    context?: Record<string, unknown> | null;
  }) =>
    api<FeedbackOut>("/api/feedback", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  /** Admin-only — non-admins get 403. */
  listAll: (status?: FeedbackStatus) =>
    api<FeedbackOut[]>(
      `/api/feedback/all${status ? `?status_filter=${status}` : ""}`,
    ),
  update: (id: number, body: Partial<{ status: FeedbackStatus; admin_note: string | null }>) =>
    api<FeedbackOut>(`/api/feedback/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
};

// ----- Bank connections -----

export interface BankFieldSpec {
  name: string;
  label: string;
  type: "text" | "password";
  optional: boolean;
  help: string | null;
  placeholder: string | null;
  /** "strip_spaces" removes ALL whitespace (user ids, BLZ); "trim" only the ends. */
  normalize: "trim" | "strip_spaces";
  /** Full-match regex on the normalized value; the backend enforces the same rule. */
  pattern: string | null;
  /** German message shown when the pattern fails. */
  pattern_error: string | null;
  /** Mobile keyboard hint, e.g. "numeric". */
  input_mode: string | null;
}

/** Apply the field's normalization exactly like the backend does. */
export function normalizeBankField(f: BankFieldSpec, value: string): string {
  return f.normalize === "strip_spaces" ? value.replace(/\s+/g, "") : value.trim();
}

/** Validate a (raw) input against the field spec. Returns the German error
 *  message, or null when the value is fine. Mirrors the backend check so the
 *  user sees problems while typing instead of after a round-trip. */
export function validateBankField(f: BankFieldSpec, value: string): string | null {
  const v = normalizeBankField(f, value);
  if (!v) return f.optional ? null : `Bitte fülle „${f.label}“ aus.`;
  if (f.pattern && !new RegExp(`^(?:${f.pattern})$`).test(v)) {
    return f.pattern_error ?? `„${f.label}“ hat nicht das erwartete Format.`;
  }
  return null;
}

export interface SupportedBank {
  id: string;
  label: string;
  available: boolean;
  description: string | null;
  fields: BankFieldSpec[];
  eta: string | null;
  fints_blz: string | null;
  fints_endpoint: string | null;
  brand_color: string | null;
  initials: string | null;
  login_url: string | null;
  tan_note: string | null;
  /** Umbrella tiles (Sparkasse, VR): open the brand-filtered bank search
   *  instead of a manual credential form. */
  search_brand: string | null;
  /** Prominenter Warnhinweis (z. B. "Anbindung wird weiterentwickelt"). */
  notice: string | null;
}

export interface ReauthStatus {
  setup: boolean;
  days_used: number | null;
  days_remaining: number | null;
  due: boolean;
  warn: boolean;
}

export interface BankConnection {
  id: number;
  household_id: number;
  account_id: number | null;
  bank_id: string;
  display_name: string;
  status: string;
  sync_frequency: "daily" | "weekly";
  last_run_at: string | null;
  next_run_at: string | null;
  last_error: string | null;
  last_balance: string | null;
  fints_setup_at: string | null;
  created_at: string;
  has_credentials: boolean;
  reauth: ReauthStatus | null;
  /** Sub-account keys (IBAN/Kontonummer) to import — null = all. */
  account_filter: string[] | null;
  /** What the last sync reported behind this login — feeds the Konten picker. */
  discovered_accounts: DiscoveredSubAccount[] | null;
}

export interface DiscoveredSubAccount {
  key: string;
  iban: string | null;
  type: string | null;
  holder: string | null;
  balance: string | null;
  depot: boolean;
}

export interface BankRunResult {
  ok: boolean;
  inserted: number;
  duplicates: number;
  auto_classified: number;
  last_balance: string | null;
  needs_reauth: boolean;
  /** Adapter wants an interactive code (e.g. Trade Republic 4-digit app
   *  code) — prompt the user and POST it via banks.challenge(). */
  needs_challenge: boolean;
  message: string;
}

export interface BankSearchResult {
  blz: string;
  name: string;
  bic: string;
  location: string;
  endpoint: string;
}

export const banks = {
  supported: () => api<SupportedBank[]>("/api/bank-connections/banks"),
  search: (q: string, brand?: string) =>
    api<BankSearchResult[]>(
      `/api/bank-connections/banks/search?q=${encodeURIComponent(q)}` +
        (brand ? `&brand=${encodeURIComponent(brand)}` : ""),
    ),
  list: () => api<BankConnection[]>("/api/bank-connections"),
  create: (body: {
    bank_id: string;
    display_name: string;
    account_id?: number | null;
    credentials?: Record<string, string> | null;
    /** "girokonto" (default) creates a transaction account; "depot" creates
     *  a securities account that syncs holdings instead of transactions. */
    account_kind?: "girokonto" | "depot" | "tagesgeld";
  }) =>
    api<BankConnection>("/api/bank-connections", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  update: (
    id: number,
    body: Partial<{
      display_name: string;
      account_id: number | null;
      credentials: Record<string, string>;
      status: string;
      sync_frequency: "daily" | "weekly";
      account_filter: string[] | null;
    }>,
  ) =>
    api<BankConnection>(`/api/bank-connections/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  remove: (id: number) =>
    api(`/api/bank-connections/${id}`, { method: "DELETE" }),
  test: (id: number) =>
    api<BankRunResult>(`/api/bank-connections/${id}/test`, { method: "POST" }),
  /** Complete an interactive login step (needs_challenge=true), e.g. the
   *  Trade Republic 4-digit app code. */
  challenge: (id: number, value: string) =>
    api<BankRunResult>(`/api/bank-connections/${id}/challenge`, {
      method: "POST",
      body: JSON.stringify({ value }),
    }),
  /** Spawn a sibling depot connection reusing the source's credentials +
   *  FinTS state — no second login / SCA required when state is still fresh. */
  cloneAsDepot: (id: number) =>
    api<BankConnection>(`/api/bank-connections/${id}/clone-as-depot`, { method: "POST" }),
  run: (id: number) =>
    api<BankRunResult>(`/api/bank-connections/${id}/run`, { method: "POST" }),
};

// ----- Classification rules -----

export interface ClassificationRule {
  id: number;
  pattern_text: string;
  pattern_field: "counterparty" | "description" | "iban";
  match_type: "contains" | "equals" | "regex" | "fuzzy";
  lev_max: number | null;
  category_id: number;
  category_name: string;
  priority: number;
  enabled: boolean;
  source: "manual" | "ai_suggested" | "ai_confirmed" | "import_seed";
  prompt: string | null;
}

export interface RuleSample {
  id: number;
  date: string | null;
  amount: string | null;
  counterparty: string;
  description: string;
}

export interface RuleProposal {
  pattern_text: string;
  pattern_field: "counterparty" | "description" | "iban";
  match_type: "contains" | "equals" | "regex" | "fuzzy";
  lev_max: number | null;
  target_category_id: number;
  target_category_name: string;
  rationale: string;
  matches_count: number;
  samples: RuleSample[];
  source: string;
}

export const rules = {
  list: () => api<ClassificationRule[]>("/api/classification-rules"),
  create: (body: {
    pattern_text: string;
    pattern_field?: "counterparty" | "description" | "iban";
    match_type?: "contains" | "equals" | "regex" | "fuzzy";
    lev_max?: number | null;
    category_id: number;
    priority?: number;
    enabled?: boolean;
  }) =>
    api<ClassificationRule>("/api/classification-rules", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  update: (id: number, body: Partial<Omit<ClassificationRule, "id" | "category_name" | "source" | "prompt">>) =>
    api<ClassificationRule>(`/api/classification-rules/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  remove: (id: number) =>
    api(`/api/classification-rules/${id}`, { method: "DELETE" }),
  preview: (body: {
    pattern_text: string;
    pattern_field: "counterparty" | "description" | "iban";
    match_type: "contains" | "equals" | "regex" | "fuzzy";
    lev_max?: number | null;
  }) =>
    api<{ matches_count: number; samples: RuleSample[] }>(
      "/api/classification-rules/preview",
      { method: "POST", body: JSON.stringify(body) },
    ),
  apply: () =>
    api<{ classified: number; rules_used: Record<string, number> }>(
      "/api/classification-rules/apply",
      { method: "POST" },
    ),
  fromTransactions: (transaction_ids: number[], category_id: number) =>
    api<ClassificationRule>("/api/classification-rules/from-transactions", {
      method: "POST",
      body: JSON.stringify({ transaction_ids, category_id }),
    }),
  flagWrong: (id: number, reason?: string) =>
    api<ClassificationRule>(`/api/classification-rules/${id}/flag-wrong`, {
      method: "POST",
      body: JSON.stringify({ reason: reason ?? null }),
    }),
  initialScan: (days = 30) =>
    api<RuleProposal[]>(`/api/classification-rules/initial-scan?days=${days}`, {
      method: "POST",
    }),
  saveProposal: (proposal: RuleProposal, enabled = true) =>
    api<ClassificationRule>("/api/classification-rules/proposals/save", {
      method: "POST",
      body: JSON.stringify({ proposal, enabled }),
    }),
};

// ----- Billing -----

export const billing = {
  status: () => api<SubscriptionInfo>("/api/billing/status"),
  refresh: () => api<SubscriptionInfo>("/api/billing/refresh", { method: "POST" }),
  checkout: (
    plan: "monthly" | "yearly",
    provider: "stripe" | "paypal" = "stripe",
    opts: { returnToNative?: boolean } = {},
  ) =>
    api<{ redirect_url: string }>("/api/billing/checkout", {
      method: "POST",
      body: JSON.stringify({
        plan,
        provider,
        return_to_native: !!opts.returnToNative,
      }),
    }),
  portal: (opts: { returnToNative?: boolean } = {}) =>
    api<{ redirect_url: string }>("/api/billing/portal", {
      method: "POST",
      body: JSON.stringify({ return_to_native: !!opts.returnToNative }),
    }),
  cancel: () => api<void>("/api/billing/cancel", { method: "POST" }),
};

export const setup = {
  seed: (body: {
    template?: string;
    primary_account_name?: string;
    bank_name?: string | null;
    monthly_net_income?: number | null;
    budget_start?: string | null;
  }) =>
    api<Household>("/api/setup/seed", {
      method: "POST",
      body: JSON.stringify({
        template: body.template ?? "standard",
        primary_account_name: body.primary_account_name ?? "Hauptkonto",
        bank_name: body.bank_name ?? "Targobank",
        monthly_net_income: body.monthly_net_income ?? null,
        budget_start: body.budget_start ?? null,
      }),
    }),
  complete: () => api<Household>("/api/setup/complete", { method: "POST" }),
  applyBudgetTier: (income: 2000 | 3500 | 5000) =>
    api<Household>(`/api/setup/apply-budget-tier?income=${income}`, {
      method: "POST",
    }),
};

// Single source of truth for the preset tiers — duplicated from the
// backend's BUDGET_PRESET_TIERS. Used by the setup wizard and the
// /budget empty-state tip.
export const BUDGET_PRESET_TIERS = [2000, 3500, 5000] as const;
export type BudgetPresetTier = (typeof BUDGET_PRESET_TIERS)[number];

// ----- Domain types -----

export interface Account {
  id: number;
  name: string;
  kind: string;
  bank_name: string | null;
  iban: string | null;
  currency: string;
  balance_cached: string;
  /** Hauptkonto: neue Budgetposten erben dieses Konto automatisch. */
  is_default: boolean;
}

export interface Category {
  id: number;
  group_id: number;
  account_id: number;
  name: string;
  monthly_budget: string;
  sort_order: number;
  archived: boolean;
  kind: "essential" | "lifestyle" | "saving" | "rucklage" | "puffer";
  company_covered: boolean;
  hide_extra_sparsam: boolean;
  target_amount: string | null;
  target_date: string | null;
  due_date: string | null;
  due_interval_months: number | null;
  cap_amount: string | null;
  member_id: number | null;
  parent_category_id: number | null;
}

export interface CategoryGroup {
  id: number;
  name: string;
  sort_order: number;
  categories: Category[];
}

export interface EnvelopeState {
  id: number;
  category_id: number;
  year: number;
  month: number;
  monatsanfang: string;
  umsaetze: string;
  budgetiert: string;
  verfuegbar: string;
  closed_at: string | null;
}

export interface SubcategoryRow {
  id: number;
  name: string;
  spent: number;
  count: number;
}

export interface EnvelopeRow {
  category: {
    id: number;
    name: string;
    monthly_budget: string;
    target_amount: string | null;
    target_date: string | null;
    due_date?: string | null;
    due_interval_months?: number | null;
    cap_amount?: string | null;
    member_id?: number | null;
    kind?: "essential" | "lifestyle" | "rucklage" | "saving" | "puffer";
    company_covered?: boolean;
    hide_extra_sparsam?: boolean;
    account_id?: number;
  };
  state: EnvelopeState;
  children?: SubcategoryRow[];
  /** Sparziel rows only: cumulative money spent out of the envelope through
   *  the shown month. Goal progress = verfuegbar + sparziel_spent — booked
   *  expenses stay attained instead of melting the bar. */
  sparziel_spent?: string | null;
}

export interface EnvelopeView {
  year: number;
  month: number;
  groups: { group: { id: number; name: string; sort_order: number }; rows: EnvelopeRow[] }[];
}

export interface Transaction {
  id: number;
  account_id: number;
  partition_id: number | null;
  date: string;
  amount: string;
  counterparty: string | null;
  description: string | null;
  category_id: number | null;
  classified_by: string | null;
  import_source: string;
  created_at: string;
  /** Set when this booking is an internal move between own pots (Überweisung
   *  aufs Zielkonto, Wertpapierkauf) — it stays out of budget and income. */
  transfer_state: "suggested" | "confirmed" | "rejected" | null;
  transfer_pair_id: number | null;
  transfer_account_id: number | null;
  /** Form der Umbuchung: invest = Wertpapierkauf, divest = Verkauf. */
  transfer_kind: "invest" | "divest" | "plain" | null;
  /** Wirkung auf den Sparbaum. "unknown" = Geld verließ ein Zielkonto Richtung
   *  eines Depots, das nicht als Zielkonto verknüpft ist — der Stand schrumpft
   *  dann still, deshalb sichtbar machen. */
  goal_effect: "increase" | "decrease" | "none" | "unknown" | null;
}

export interface Reconciliation {
  account_id: number;
  account_name: string;
  kontostand: string;
  sum_envelope_verfuegbar: string;
  buffer: string;
}

// ----- Endpoints -----

export const accounts = {
  list: () => api<Account[]>("/api/accounts"),
  create: (body: {
    name: string;
    kind?: string;
    bank_name?: string | null;
    iban?: string | null;
    opening_balance?: string;
  }) =>
    api<Account>("/api/accounts", {
      method: "POST",
      body: JSON.stringify({
        name: body.name,
        kind: body.kind ?? "girokonto",
        bank_name: body.bank_name ?? null,
        iban: body.iban ?? null,
        opening_balance: body.opening_balance ?? "0.00",
      }),
    }),
  update: (id: number, body: Partial<{ balance_cached: string; iban: string; bank_name: string; name: string; is_default: boolean }>) =>
    api<Account>(`/api/accounts/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  /** Delete an account. Without `withData` the backend 409s when the account
   *  still carries Umsätze / Depot-Positionen / eine Bank-Verbindung — the
   *  UI then offers the escalation and retries with `withData: true`, which
   *  deletes everything on the account (envelopes are corrected). */
  remove: (id: number, opts: { withData?: boolean } = {}) =>
    api<{
      status: string;
      account_id: number;
      categories_moved: number;
      categories_moved_to: string | null;
      deleted_transactions: number;
      deleted_connections: number;
    }>(`/api/accounts/${id}${opts.withData ? "?with_data=true" : ""}`, {
      method: "DELETE",
    }),
};

export const categories = {
  groups: () => api<CategoryGroup[]>("/api/category-groups"),
  createGroup: (body: { name: string; sort_order?: number }) =>
    api<CategoryGroup>("/api/category-groups", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  updateGroup: (id: number, body: Partial<{ name: string; sort_order: number }>) =>
    api<CategoryGroup>(`/api/category-groups/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  deleteGroup: (id: number) =>
    api(`/api/category-groups/${id}`, { method: "DELETE" }),
  create: (body: {
    name: string;
    group_id: number;
    account_id?: number | null;
    monthly_budget?: string;
    kind?: "essential" | "lifestyle" | "rucklage" | "saving" | "puffer";
    company_covered?: boolean;
    parent_category_id?: number | null;
    sort_order?: number;
    target_amount?: string | null;
    target_date?: string | null;
    due_date?: string | null;
    due_interval_months?: number | null;
    cap_amount?: string | null;
  }) =>
    api<Category>("/api/categories", {
      method: "POST",
      body: JSON.stringify({
        name: body.name,
        group_id: body.group_id,
        account_id: body.account_id ?? null,
        monthly_budget: body.monthly_budget ?? "0.00",
        kind: body.kind ?? "essential",
        company_covered: body.company_covered ?? false,
        parent_category_id: body.parent_category_id ?? null,
        sort_order: body.sort_order ?? 0,
        target_amount: body.target_amount ?? null,
        target_date: body.target_date ?? null,
        due_date: body.due_date ?? null,
        due_interval_months: body.due_interval_months ?? null,
        cap_amount: body.cap_amount ?? null,
      }),
    }),
  update: (
    id: number,
    body: Partial<{
      name: string;
      monthly_budget: string;
      kind: "essential" | "lifestyle" | "rucklage" | "saving" | "puffer";
      company_covered: boolean;
      hide_extra_sparsam: boolean;
      target_amount: string | null;
      target_date: string | null;
      due_date: string | null;
      due_interval_months: number | null;
      cap_amount: string | null;
      archived: boolean;
      group_id: number;
      sort_order: number;
      account_id: number;
      parent_category_id: number | null;
    }>,
  ) =>
    api<Category>(`/api/categories/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  /** Soft-delete (archive=true). Categories stay in the DB (history intact)
   *  but disappear from /budget + /einstellungen lists. Un-archive via
   *  update(id, { archived:false }). */
  archive: (id: number) =>
    api<Category>(`/api/categories/${id}`, {
      method: "PATCH",
      body: JSON.stringify({ archived: true }),
    }),
  /** Hard-delete: removes the category for good, releases its transactions
   *  back to "unkategorisiert" and drops its per-month states + rules. */
  deleteHard: (id: number) =>
    api<{ released_transactions: number }>(`/api/categories/${id}`, {
      method: "DELETE",
    }),
  /** Persist a drag&drop result: full display order of one group; categories
   *  from other groups are moved in. Einkommen↔Ausgaben moves are rejected. */
  reorder: (group_id: number, ordered_category_ids: number[]) =>
    api<{ updated: number }>(`/api/categories/reorder`, {
      method: "POST",
      body: JSON.stringify({ group_id, ordered_category_ids }),
    }),
};

/** Public update gate — no auth. Compared against the baked-in bundle
 *  version at startup (see UpdateGate). */
export interface Announcement {
  id: number;
  message: string;
  level: "info" | "warn";
  active: boolean;
  created_by: string | null;
  created_at: string;
}

export const announcements = {
  active: () => api<Announcement | null>("/api/announcements/active"),
  list: () => api<Announcement[]>("/api/announcements"),
  create: (body: { message: string; level?: "info" | "warn"; active?: boolean }) =>
    api<Announcement>("/api/announcements", { method: "POST", body: JSON.stringify(body) }),
  update: (id: number, body: Partial<{ message: string; level: "info" | "warn"; active: boolean }>) =>
    api<Announcement>(`/api/announcements/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
};

export const appMeta = {
  version: () =>
    api<{ min_version: string | null; update_url: string }>(`/api/app-version`),
};

export const envelopes = {
  view: (year: number, month: number) =>
    api<EnvelopeView>(`/api/envelopes/${year}/${month}`),
  updateState: (stateId: number, budgetiert: string) =>
    api<EnvelopeState>(`/api/envelopes/states/${stateId}`, {
      method: "PATCH",
      body: JSON.stringify({ budgetiert }),
    }),
  /** from_state_id null = aus dem Spielraum des Kontos decken (nur Ziel wird
   *  aufgestockt, kein anderes Envelope reduziert). */
  cover: (from_state_id: number | null, to_state_id: number, amount: string) =>
    api(`/api/envelopes/cover`, {
      method: "POST",
      body: JSON.stringify({ from_state_id, to_state_id, amount }),
    }),
  closeMonth: (year: number, month: number) =>
    api(`/api/envelopes/close-month/${year}/${month}`, { method: "POST" }),
  extraSparsam: (state_id: number) =>
    api<{ amount: string; state: EnvelopeState }>(
      `/api/envelopes/states/${state_id}/extra-sparsam`,
      { method: "POST" },
    ),
};

export const transactions = {
  list: (params: Record<string, string | number | undefined> = {}) => {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== "") q.set(k, String(v));
    });
    return api<Transaction[]>(`/api/transactions?${q.toString()}`);
  },
  create: (body: {
    account_id: number;
    date: string;
    amount: string;
    counterparty?: string;
    description?: string;
    category_id?: number;
  }) => api<Transaction>(`/api/transactions`, { method: "POST", body: JSON.stringify(body) }),
  update: (
    id: number,
    body: Partial<{ amount: string; date: string; category_id: number; counterparty: string; description: string }>,
    opts: { force?: boolean } = {},
  ) =>
    api<Transaction>(`/api/transactions/${id}${opts.force ? "?force=true" : ""}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  remove: (id: number) =>
    api(`/api/transactions/${id}`, { method: "DELETE" }),
  accept: (id: number) =>
    api<Transaction>(`/api/transactions/${id}/accept`, { method: "POST" }),
  recentSummary: (sinceIso: string) =>
    api<{ count: number; latest_created_at: string | null; server_time: string }>(
      `/api/transactions/recent-summary?since=${encodeURIComponent(sinceIso)}`,
    ),
  count: (from?: string, to?: string) => {
    const q = new URLSearchParams();
    if (from) q.set("from", from);
    if (to) q.set("to", to);
    return api<{ total: number; uncategorized: number }>(
      `/api/transactions/count?${q.toString()}`,
    );
  },
};

export interface SpendingTrend {
  groups: string[];
  rows: Record<string, string | number>[];
}

export interface SpendingShareRow {
  category: string;
  group: string;
  amount: number;
}

export interface SankeyNode {
  name: string;
  kind: string;
  color?: string;
}

export interface SankeyData {
  year: number;
  month: number;
  income_total: number;
  income_buckets: Record<string, number>;
  nodes: SankeyNode[];
  links: { source: number; target: number; value: number }[];
}

export type MemberPalette = Record<string, { label: string; color: string }>;

export const reports = {
  reconciliation: (account_id: number) =>
    api<Reconciliation>(`/api/reports/reconciliation?account_id=${account_id}`),
  spendingTrend: (months = 6, offsetMonths = 0) =>
    api<SpendingTrend>(
      `/api/reports/spending-trend?months=${months}&offset_months=${offsetMonths}`,
    ),
  spendingShare: (year: number, month: number) =>
    api<SpendingShareRow[]>(`/api/reports/spending-share?year=${year}&month=${month}`),
  sankey: (year: number, month: number) =>
    api<SankeyData>(`/api/reports/sankey?year=${year}&month=${month}`),
  budgetSummary: (year: number, month: number) =>
    api<Record<string, { budgetiert: number; spent: number; count: number }>>(
      `/api/reports/budget-summary?year=${year}&month=${month}`,
    ),
  goalMonthly: (year: number, month: number) =>
    api<{ total: number; by_source: Record<string, number> }>(
      `/api/reports/goal-monthly-contribution?year=${year}&month=${month}`,
    ),
  memberPalette: () => api<MemberPalette>(`/api/reports/member-palette`),
  allocation: () => api<Allocation>(`/api/reports/allocation`),
};

export interface Allocation {
  total_konten: number;
  buckets: {
    alltag: number;
    sparen: number;
    sparziele: number;
    rucklagen: number;
    puffer: number;
    grosses_ziel: number;
  };
  frei: number;
}

export interface SparzielRow {
  id: number;
  name: string;
  monthly_budget: string;
  target_amount: string;
  target_date: string | null;
  current_verfuegbar: string;
  /** Cumulative expenses booked on the goal — they count as attained. */
  spent: string;
  /** current_verfuegbar + spent; the number progress is measured with. */
  attained: string;
  remaining: string;
  months_left: number | null;
  past_due: boolean;
  suggested_monthly: string | null;
  progress: number;
  on_track: boolean;
}

export const sparziele = {
  list: () => api<SparzielRow[]>("/api/sparziele"),
  create: (body: { name: string; target_amount: string; target_date: string; group_name?: string }) =>
    api<{
      id: number;
      name: string;
      target_amount: string;
      target_date: string;
      monthly_budget: string;
      group: string;
      months_left: number;
    }>("/api/sparziele", { method: "POST", body: JSON.stringify(body) }),
  /** Sparziele are Category rows under the hood — update + archive go through
   *  the standard categories endpoints. */
  update: (
    id: number,
    body: Partial<{ name: string; target_amount: string; target_date: string; monthly_budget: string }>,
  ) => categories.update(id, body),
  remove: (id: number) => categories.archive(id),
};

// ----- Goals -----

/**
 * `by_source` keys take one of three forms:
 *   - "joint"           → member_id NULL contributions (gemeinsam)
 *   - "member:<id>"     → contributions by a specific household member
 *   - "account:<id>"    → balance contribution from a linked Zielkonto
 * Resolve them via /api/reports/member-palette + accounts.list() to get
 * label + color.
 */
export interface GoalNode {
  id: number;
  parent_goal_id: number | null;
  name: string;
  target_amount: string;
  sort_order: number;
  archived: boolean;
  direct_total: string;
  total: string;
  total_target: string;
  progress: number;
  by_source: Record<string, string>;
  /** Accounts hooked up as Zielkonten — their value rolls into `total`.
   *  A depot counts with its market value, so money moved into a
   *  Geldmarktfonds stays visible instead of vanishing from the goal. */
  linked_account_ids: number[];
  /** Value of this node's own Zielkonten, before anything is handed down. */
  pot: string;
  /** Share of an ancestor's pot earmarked for this node. */
  allocated: string;
  /** The rule behind `allocated`, or null when the user set none. */
  allocation: { mode: "amount" | "percent"; value: string } | null;
  /** Part of `pot` no child claims — "noch nicht zugeordnet". */
  unallocated: string;
  /** The rules asked for more than the pot holds; shares were scaled down. */
  over_allocated: boolean;
  /** Vorgemerkt, liegt noch auf einem Alltagskonto — dieser Betrag kann aufs
   *  Zielkonto überwiesen ("mitgenommen") werden. */
  open_contributions: string;
  /** Bereits aufs Zielkonto mitgenommen: zählt nicht mehr zusätzlich, färbt
   *  aber den Pot nach Mitglied. */
  settled_contributions: string;
  /** open_contributions dieses Ziels plus aller Unterziele. */
  open_contributions_total: string;
  children: GoalNode[];
}

export interface GoalContribution {
  id: number;
  goal_id: number;
  account_id: number | null;
  partition_id: number | null;
  amount: string;
  member_id: number | null;
  date: string;
  note: string | null;
  transaction_id: number | null;
}

export const goals = {
  tree: () => api<GoalNode[]>("/api/goals"),
  one: (id: number) => api<GoalNode>(`/api/goals/${id}`),
  create: (body: { parent_goal_id?: number | null; name: string; target_amount: string }) =>
    api<GoalNode>("/api/goals", { method: "POST", body: JSON.stringify(body) }),
  update: (id: number, body: Partial<{ name: string; target_amount: string; archived: boolean }>) =>
    api<GoalNode>(`/api/goals/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  archive: (id: number) => api(`/api/goals/${id}`, { method: "DELETE" }),
  contributions: (id: number) =>
    api<GoalContribution[]>(`/api/goals/${id}/contributions`),
  addContribution: (
    id: number,
    body: { amount: string; member_id?: number | null; date: string; note?: string },
  ) =>
    api<GoalContribution>(`/api/goals/${id}/contributions`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  reassignContribution: (cid: number, goal_id: number) =>
    api<GoalContribution>(`/api/goal-contributions/${cid}`, {
      method: "PATCH",
      body: JSON.stringify({ goal_id }),
    }),
  deleteContribution: (cid: number) =>
    api(`/api/goal-contributions/${cid}`, { method: "DELETE" }),
  /** Link an account as a Zielkonto for this goal. Its balance counts
   *  toward the goal's total. Idempotent. */
  linkAccount: (goal_id: number, account_id: number) =>
    api<{ goal_id: number; account_id: number }>(
      `/api/goals/${goal_id}/accounts`,
      { method: "POST", body: JSON.stringify({ account_id }) },
    ),
  unlinkAccount: (goal_id: number, account_id: number) =>
    api(`/api/goals/${goal_id}/accounts/${account_id}`, { method: "DELETE" }),
  /** Earmark part of an ancestor's Zielkonto-Pot for this goal. Redistributes
   *  the pot — it never adds money the household does not have. */
  setAllocation: (goal_id: number, mode: "amount" | "percent", value: string) =>
    api<GoalNode>(`/api/goals/${goal_id}/allocation`, {
      method: "PUT",
      body: JSON.stringify({ mode, value }),
    }),
  clearAllocation: (goal_id: number) =>
    api<GoalNode>(`/api/goals/${goal_id}/allocation`, { method: "DELETE" }),
  /** Vorgemerkte Rücklage als aufs Zielkonto überwiesen markieren. Ohne
   *  `amount` wird alles Offene des Teilbaums verrechnet. Verhindert, dass die
   *  mitgenommene Rücklage doppelt zählt (Beitrag + Kontostand). */
  settleContributions: (goal_id: number, amount?: string, transaction_id?: number) =>
    api<{ settled_amount: string; rows_touched: number; goal: GoalNode }>(
      `/api/goals/${goal_id}/contributions/settle`,
      { method: "POST", body: JSON.stringify({ amount, transaction_id }) },
    ),
  unsettleContributions: (goal_id: number) =>
    api<GoalNode>(`/api/goals/${goal_id}/contributions/unsettle`, { method: "POST" }),
};

// ----- Umbuchungen (internal transfers) -----

export interface TransferRow {
  id: number;
  date: string;
  amount: string;
  counterparty: string | null;
  description: string | null;
  account_id: number;
  transfer_state: "suggested" | "confirmed" | "rejected" | null;
  transfer_pair_id: number | null;
  transfer_account_id: number | null;
  transfer_account_name: string | null;
}

export interface TransferSuggestion {
  outflow_id: number;
  inflow_id: number;
  amount: string;
  outflow_account_id: number;
  inflow_account_id: number;
  reason: string;
}

export const transfers = {
  /** Detected Umbuchungen awaiting review (default) or a given state. */
  list: (state?: "suggested" | "confirmed" | "rejected") =>
    api<TransferRow[]>(`/api/transfers${state ? `?state=${state}` : ""}`),
  /** Amount+date matches without IBAN evidence — offered, never auto-applied. */
  suggestions: () => api<TransferSuggestion[]>("/api/transfers/suggestions"),
  detect: (full_history = false) =>
    api<{ pairs: number; securities: number }>(
      `/api/transfers/detect?full_history=${full_history}`,
      { method: "POST" },
    ),
  confirm: (id: number) =>
    api<TransferRow>(`/api/transfers/${id}/confirm`, { method: "POST" }),
  /** "Das ist keine Umbuchung" — puts the booking back into the budget. */
  reject: (id: number) =>
    api<TransferRow[]>(`/api/transfers/${id}/reject`, { method: "POST" }),
  /** Buchung von Hand als Umbuchung auf ein Gegenkonto setzen (Picker). */
  mark: (tx_id: number, account_id: number) =>
    api<TransferRow[]>(`/api/transfers/${tx_id}/mark`, {
      method: "POST",
      body: JSON.stringify({ account_id }),
    }),
  pair: (outflow_id: number, inflow_id: number) =>
    api<TransferRow[]>("/api/transfers/pair", {
      method: "POST",
      body: JSON.stringify({ outflow_id, inflow_id }),
    }),
};

// ----- Dashboard (customizable Übersicht) -----

export type WidgetSize = "full" | "half";

export interface DashboardWidget {
  type: string;
  size: WidgetSize;
  config: Record<string, unknown>;
}

export interface DashboardLayout {
  version: number;
  widgets: DashboardWidget[];
}

export const dashboard = {
  get: () => api<{ layout: DashboardLayout | null }>("/api/dashboard-layout"),
  put: (layout: DashboardLayout | null) =>
    api<{ layout: DashboardLayout | null }>("/api/dashboard-layout", {
      method: "PUT",
      body: JSON.stringify({ layout }),
    }),
};

// ----- Helpers for member-aware UIs -----

export function sourceKey(member_id: number | null): string {
  return member_id === null ? "joint" : `member:${member_id}`;
}

export function memberIdFromKey(key: string): number | null {
  if (key === "joint") return null;
  if (key.startsWith("member:")) return Number(key.slice("member:".length));
  return null;
}

// ----- Imports -----

export interface ImportRunRow {
  idx: number;
  date: string;
  amount: string;
  counterparty: string | null;
  description: string | null;
  dedupe_key: string;
  duplicate: boolean;
  suggested_category_id: number | null;
  raw: Record<string, string>;
}

export interface ImportRunSummary {
  account_id: number;
  row_count: number;
  duplicates: number;
  auto_classified: number;
  rows: ImportRunRow[];
  committed?: { inserted: number; skipped_duplicate: number; skipped_by_user: number };
}

export interface ImportRun {
  id: number;
  status: string;
  source: string;
  file_name: string | null;
  started_at: string | null;
  finished_at: string | null;
  summary: ImportRunSummary;
}

export const imports = {
  upload: async (account_id: number, file: File): Promise<ImportRun & { rows?: ImportRunRow[]; row_count?: number }> => {
    const fd = new FormData();
    fd.append("account_id", String(account_id));
    fd.append("file", file);
    const tok = await refreshIfStale();
    const res = await fetch(apiUrl("/api/import/csv"), {
      method: "POST",
      body: fd,
      credentials: "include",
      headers: tok ? { Authorization: `Bearer ${tok.access}` } : undefined,
    });
    const body = await res.json();
    if (!res.ok) throw new ApiError(res.status, body);
    return body;
  },
  get: (id: number) => api<ImportRun>(`/api/import-runs/${id}`),
  commit: (id: number, decisions: Array<{ idx: number; category_id?: number | null; skip?: boolean; override_duplicate?: boolean }>) =>
    api(`/api/import-runs/${id}/commit`, { method: "POST", body: JSON.stringify(decisions) }),
  discard: (id: number) => api(`/api/import-runs/${id}`, { method: "DELETE" }),
};
